import { Button, Modal } from '@mui/material';
import RecordRTC from 'recordrtc';
import { storage } from 'firebaseConfig';
import { ref as fireRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    getAudioExtensionFromMime,
    normalizeAudioMimeType,
} from 'components/NativeAudioPlayer';
import moment from 'moment';
import React, { useEffect, useRef, useState, useImperativeHandle, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { v4 } from 'uuid';
import Loading from "components/Loading";
import COLOR from 'utils/constants/color';
import { lighten } from 'polished';
import WaveformVisualizer from 'components/WaveformVisualizer';
import { PauseCircleFilledOutlined, PlayArrowOutlined, RecordVoiceOverOutlined } from '@mui/icons-material';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import TranscriptWithAudio from 'components/UI/transcriptWithAudio/transcriptWithAudio';
// Styled Components (same as before)
//  ----------------------------------------------------------------------------
const ModalContent = styled.div`
    align-items: center;
    background-color: ${COLOR.white};
    border-radius: 3px;
    display: inline-flex;
    flex-direction: column;
    left: 50%;
    max-width: 320px;
    padding: 16px;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
`;

const ModalRow = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-top: 16px;
    width: 100%;
`;

const Container = styled.div`
    // padding: 16px;
    overflow: hidden;
    width: 100%;
    display: flex;
    // flex-direction: column;
    align-items: center;
    // justify-content: center;
`;

const Counter = styled.span`
    color: ${COLOR.green100};
    display: block;
    font-size: 32px;
    font-weight: 600;
    // line-height: 80px;
    // margin-bottom: 16px;
    text-align: center;
    margin-right: 20px;
    margin-left: 20px;

    @media screen and (max-width: 400px) {
        margin-left: 0px;
        margin-right: 10px!important;
    }
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    // margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0px;
    }
`;

const Text = styled.span`
    color: ${COLOR.black300};
    display: block;
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 24px;
    text-align: center;
`;

const Disclosure = styled.span`
    color: ${COLOR.yellow};
    display: block;
    font-size: 12px;
    line-height: 12px;
    margin-bottom: 10px;
    text-align: center;
`;

const Warning = styled.div`
    align-items: center;
    background-color: ${lighten(0.45, 'red')};
    border: 1px dashed red;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    justify-content: center;
    padding: 16px;
    text-align: center;
    width: 100%;
`;

const Wrapper = styled.div`
    border-bottom: 1px dashed ${COLOR.black700};
    margin-bottom: 8px;
    padding: 0px 0px 24px;
    width: 100%;
`;
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.6;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const RecordingIndicator = styled.div`
  width: 20px;
  height: 20px;
  background-color: red;
  border-radius: 50%;
  animation: ${pulse} 1s infinite;
  margin-bottom: 16px;
`;

let recorder; // Global reference to RecordRTC instance

const AudioRecorder = React.forwardRef(({
    handleAudio,
    maxTime,
    message,
    refresh,
    storageLoc,
    isPreview = false,
    source,
    wrapperStyle = {},
    hasSaveButton,
    doneButton,
    type = "",
    isMobileDevice=false,
    disabled=false
}, ref) => {
    const [AllowAudio, setAllowAudio] = useState(false);
    const [ErrorAllowAudio, setErrorAllowAudio] = useState(false);
    const [audio, setAudio] = useState(source || null);
    const [counter, setCounter] = useState(maxTime || null);
    const [recording, setRecording] = useState(false);
    const [noAccess, setNoAccess] = useState(false);
    const [noPermission, setNoPermission] = useState(false);
    const [inUrlProcess, setInUrlProcess] = useState(false);
    const [paused, setPaused] = useState(false);
    const [stream, setStream] = useState(null); // State to store the audio stream
    const [blob, setBlob] = useState(null); // To store the recorded blob
    const timmerWorkerRef = useRef(null); 
    const saveButtonRef = useRef(null);
    const assignment_review_submission = (type === "assignment-review") && isMobileDevice; 

    useImperativeHandle(ref, () => ({
        pause: onPause,
        resume: onResume,
        stop: stopRecord,
        cancel: cancelRecording,
        reset: resetRecording,
        start: startRecord,
        setAudio: setAudio
    }));

    useEffect(() => {
        if (source && source.length > 0) {
            setAudio(source);
            setRecording(false);
        }
    }, [source]);

    useEffect(() => {
        try {
            if (isPreview) {
                setNoAccess(false);
                setNoPermission(false);
                return;
            }
            const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
            if (navigator.permissions && !isFirefox) {
                navigator.permissions.query({ name: 'microphone' }).then(permissionStatus => {
                    if (permissionStatus.state === 'denied') {
                        setNoAccess(false);
                        setNoPermission(true);
                    }
                    permissionStatus.onchange = function () {
                        if (this.state === 'denied') {
                            setNoAccess(false);
                            setNoPermission(true);
                        } else {
                            setNoAccess(false);
                            setNoPermission(false);
                        }
                    };
                });
            }
            if (navigator?.mediaDevices && !isFirefox) {
                navigator.mediaDevices.getUserMedia({ audio: true })
                    .then((stream) => {
                        stream.getTracks().forEach(_tr => _tr.stop());
                        setNoAccess(false);
                        setNoPermission(false);
                    })
                    .catch(() => {
                        setNoAccess(true);
                        setNoPermission(false);
                    });
            }
        } catch (error) {
            setNoPermission(true);
        }
    }, []);

    const startRecord = () => {
        if (!recording && !isPreview) {
            setAllowAudio(true);
            navigator.mediaDevices.getUserMedia({ audio: true })
                .then((stream) => {
                    setAllowAudio(false);
                    setStream(stream);
                    recorder = new RecordRTC(stream, { type: 'audio' });
                    recorder.startRecording();
                    setRecording(true);
                    timmerWorkerRef.current.postMessage({ command: 'start', time: maxTime }); 
                })
                .catch((err) => {
                    setAllowAudio(false);
                    setErrorAllowAudio(true);
                    console.log(`${err.name} : ${err.message}`);
                });
        }
    };

    const stopRecord = async () => {
        if (recording && !isPreview) {
            timmerWorkerRef.current.postMessage({ command: 'stop' }); 
            setInUrlProcess(true)
            try {
                await new Promise((resolve) => {
                    recorder.stopRecording(async () => {
                        const audioBlob = recorder.getBlob();
                        const blobUrl = URL.createObjectURL(audioBlob);
                        if (stream) {
                            stream.getTracks().forEach(track => track.stop());
                        }
                        setAudio(blobUrl);
                        setBlob(audioBlob);
                        await uploadToStorage(audioBlob);
                        resolve(audioBlob);
                    });
                })
            } catch (error) {

                console.log(error,"error")
                setRecording(false);
                setInUrlProcess(false)
            }
            setInUrlProcess(false)
            setRecording(false);
        }
    };

    const onPause = () => {
        if (recording) {
            recorder.pauseRecording();
            setPaused(true);
            timmerWorkerRef.current.postMessage({ command: 'stop' }); 
        }
    };

    const onResume = () => {
        if (recording) {
            recorder.resumeRecording();
            setPaused(false);
            timmerWorkerRef.current.postMessage({ command: 'start', time: counter }); 
        }
    };

    const cancelRecording = () => {
        if (isPreview || inUrlProcess) {
            return;
        }
        if (!recording && !stream) {
            return;
        }
        try {
            timmerWorkerRef.current?.postMessage({ command: 'stop' });
        } catch (_) {}

        const stopTracks = () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };

        if (recording && recorder) {
            try {
                recorder.stopRecording(() => {
                    stopTracks();
                });
            } catch (_) {
                stopTracks();
            }
        } else {
            stopTracks();
        }

        setStream(null);
        setBlob(null);
        setRecording(false);
        setPaused(false);
        setInUrlProcess(false);
        try {
            timmerWorkerRef.current?.postMessage({ command: 'reset' });
        } catch (_) {}
        setCounter(null);
        if (typeof source === 'string' && source.length > 0) {
            setAudio(source);
        } else {
            setAudio(null);
        }
    };

    const resetRecording = () => {
        setCounter(null);
        setAudio(null);
        setRecording(false);
        setPaused(false);
        timmerWorkerRef.current.postMessage({ command: 'reset' });
    };

    useEffect(() => {
        timmerWorkerRef.current =  new Worker(`${(import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')}timeWorker.js?t=6`);
        timmerWorkerRef.current.onmessage = (event) => {
            const { type, countdownTime } = event.data;
            if (type === 'tick') {
                setCounter(countdownTime); 
            } else if (type === 'done') {
                if(saveButtonRef.current){
                    saveButtonRef.current.click();
                }  
                setCounter(0);  
            } else if (type === 'reset') {
                setCounter(maxTime);  
            }
        };
        return () => {
            if (timmerWorkerRef.current) {
                timmerWorkerRef.current.terminate();
            }
        };
    }, [maxTime]);
    const uploadToStorage = async (blob) => {
        if (blob) {
            const location = storageLoc || '/audio/activity-submissions';
            const id = `${moment().format('YYYY-MM-DD')}-${v4()}`;
            const contentType = normalizeAudioMimeType(blob.type) || 'audio/webm';
            const extension = getAudioExtensionFromMime(contentType);
            const audioRef = fireRef(storage, `${location}/${id}${extension}`);
            try {
                await uploadBytes(audioRef, blob, { contentType });
                const url = await getDownloadURL(audioRef);
                setData(url);
            } catch (error) {
                console.error("Upload error:", error);
            }
        }
    };

    const setData = (url) => {
        setInUrlProcess(false);
        setAudio(url);
        handleAudio(url);
    };

    const secondsToTime = (secs, description = false) => {
        let hours = Math.floor(secs / (60 * 60));
        let divisor_for_minutes = secs % (60 * 60);
        let minutes = Math.floor(divisor_for_minutes / 60);
        let divisor_for_seconds = divisor_for_minutes % 60;
        let seconds = Math.ceil(divisor_for_seconds);
        let string = "";
        if (description) {
            if (hours > 0) {
                string += hours + " hours, ";
            }
            if (minutes > 0) {
                string += minutes + " minutes";
            }
            if (seconds > 0) {
                string += ", " + (seconds < 10 ? "0" : "") + seconds + " seconds";
            }
        } else {
            if (hours > 0) {
                string += hours + ":";
            }
            if (minutes > 0) {
                string += (minutes < 10 ? "0" : "") + minutes;
            } else {
                string += "00";
            }
            if (seconds > 0) {
                string += ":" + (seconds < 10 ? "0" : "") + seconds;
            } else {
                string += ":00";
            }
        }
        return string;
    };
    return (
        <Wrapper style={wrapperStyle}>
            <Modal open={AllowAudio} onClose={() => setAllowAudio(false)}>
                <ModalContent>
                    To record the audio, click “Allow” above to give access to your computer’s microphone.
                </ModalContent>
            </Modal>
            <Modal open={ErrorAllowAudio} onClose={() => setErrorAllowAudio(false)}>
                <ModalContent>
                    To record the audio, you need access to your computer's microphone. Click “Allow” to give access.
                    <ModalRow>
                        <Button color="primary" variant="contained" onClick={() => setErrorAllowAudio(false)}>
                            Ok, I got it!
                        </Button>
                    </ModalRow>
                </ModalContent>
            </Modal>
            <Container style={{marginTop: assignment_review_submission && !(noAccess || noPermission) ? "6px" : ""  }}>
                {noAccess || noPermission ? (
                    <Warning className={`${assignment_review_submission ? "mic-permission-for-review mic-permission-text  mt-0px" : ""}`}>
                        {noPermission ? 'Please grant access to your microphone to ensure proper recording functionality.' : ''}
                        {noAccess && !noPermission ? 'Please connect your microphone to ensure optimal recording functionality.' : ''}
                        {/* <Button variant='contained' onClick={refresh} style={{ backgroundColor: 'red', color: 'white', marginTop: '16px', width: '100%' }}>Refresh</Button> */}
                    </Warning>
                ) : (
                    <>
                     {stream && recording && !assignment_review_submission && <div  style={{ width: '40%', overflow:"hidden" }}><WaveformVisualizer stream={stream} isRecording={recording} paused={paused} /></div>}
                        {(!recording && audio) ? (
                            <div className={`${assignment_review_submission ? "reRecord-container" : ""}`} style={{display:"flex", alignItems:"center", flexWrap: 'wrap'}}>
                                <Row style={{ minWidth: 200, textAlign: 'left', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                                    <TranscriptWithAudio audioUrl={audio} title={audio}>
                                    <audio className={`${assignment_review_submission ? "audio-player-student-review" : ""}`} controls controlsList={"nodownload"} src={audio} style={{ height: '40px' }}></audio>
                                    </TranscriptWithAudio>
                                    <Button className='reRecordBtn' onClick={() => resetRecording()} style={{ marginLeft: '8px' }}>Re-record</Button>
                                    {doneButton ? doneButton : null}
                                </Row>
                            </div>
                        ) : (
                            <>
                                {counter === null && (
                                    <>
                                        <Text style={{ marginBottom: '5px' }}>{message ?? `Click "Record" below. You will have ${secondsToTime(maxTime, true)} to record your answer.`}</Text>
                                        {/* <Disclosure>Place your microphone close for better audio quality.</Disclosure> */}
                                    </>
                                )}
                                {(maxTime && counter > 0) && (
                                    <>
                                        {/* <Disclosure>Place your microphone close for better audio quality.</Disclosure> */}
                                        <Counter className={`${assignment_review_submission ? "ft-17px" : ""}`}>{secondsToTime(counter)}</Counter>
                                    </>
                                )}
                                <Row>
                                    {assignment_review_submission ? (
                                        <button
                                        disabled={inUrlProcess || isPreview || inUrlProcess}
                                        color="primary"
                                        variant='contained'
                                        className="record-button-student-review items-center flex"
                                        onClick={() => {
                                            if (!isPreview) {
                                                if (recording && paused) {
                                                    onResume();
                                                } else if (recording && !paused) {
                                                    onPause();
                                                } else {
                                                    startRecord();
                                                }
                                            }
                                        }}
                                        style={{ marginRight: '8px' }}
                                    >
                                        {recording && paused ? <PlayArrowOutlined style={{color: "#03989e"}} /> :recording && !paused  ? <PauseCircleFilledOutlined style={{color:  "#03989e"}} /> : <RecordVoiceOverOutlined style={{color: "#03989e"}} />}
                                        {/* {recording && paused ? "Resume" : recording && !paused ? "Pause" : "Record"} */}
                                    </button>   

                                    ): (
                                        <Button
                                        disabled={inUrlProcess || isPreview || inUrlProcess || disabled}
                                        color="primary"
                                        variant='contained'
                                        onClick={() => {
                                            if (!isPreview) {
                                                if (recording && paused) {
                                                    onResume();
                                                } else if (recording && !paused) {
                                                    onPause();
                                                } else {
                                                    startRecord();
                                                }
                                            }
                                        }}
                                        style={{ marginRight: '12px' }}
                                    >
                                        {recording && paused ? "Resume" : recording && !paused ? "Pause" : "Record"}
                                    </Button>
                                    )}
                                    <Button
                                        disabled={!recording || inUrlProcess || isPreview}
                                        variant="outlined"
                                        color="inherit"
                                        onClick={cancelRecording}
                                        style={{
                                            marginRight: assignment_review_submission ? '8px' : '12px',
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button disabled={!recording || (maxTime && maxTime === counter) || inUrlProcess || disabled} ref={saveButtonRef} onClick={() => stopRecord()}>
                                    {inUrlProcess && <Loading  size={14} />}
                                    {hasSaveButton ? "Save" : "Stop"}</Button>
                                </Row>
                            </>
                        )}
                    </>
                )}
            </Container>
        </Wrapper>
    );
});

export default AudioRecorder;